/**
 * Simplified FilterPanel component for testing
 * Uses basic HTML elements to avoid complex component dependencies
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';

const SimpleFilterPanel = ({ 
  onFiltersChange, 
  availableOptions = {}, 
  currentFilters = {},
  className = ""
}) => {
  const [localFilters, setLocalFilters] = useState({
    dateRange: { start: null, end: null },
    daysOfWeek: [],
    states: [],
    tracks: [],
    combinedOddsThreshold: null,
    horseCountRange: { min: null, max: null },
    ...currentFilters
  });

  const stateOptions = availableOptions.states || ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
  const trackOptions = availableOptions.tracks || [];
  const daysOfWeekOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const handleFilterChange = useCallback((filterType, value) => {
    const updatedFilters = {
      ...localFilters,
      [filterType]: value
    };
    setLocalFilters(updatedFilters);
    if (onFiltersChange) {
      onFiltersChange(updatedFilters);
    }
  }, [localFilters, onFiltersChange]);

  const handleDateRangeChange = useCallback((type, value) => {
    const updatedDateRange = {
      ...localFilters.dateRange,
      [type]: value
    };
    handleFilterChange('dateRange', updatedDateRange);
  }, [localFilters.dateRange, handleFilterChange]);

  const handleHorseCountChange = useCallback((type, value) => {
    const updatedRange = {
      ...localFilters.horseCountRange,
      [type]: value ? parseInt(value) : null
    };
    handleFilterChange('horseCountRange', updatedRange);
  }, [localFilters.horseCountRange, handleFilterChange]);

  const handleMultiSelectChange = useCallback((filterType, option, checked) => {
    const currentValues = localFilters[filterType] || [];
    const updatedValues = checked
      ? [...currentValues, option]
      : currentValues.filter(v => v !== option);
    handleFilterChange(filterType, updatedValues);
  }, [localFilters, handleFilterChange]);

  const clearAllFilters = useCallback(() => {
    const clearedFilters = {
      dateRange: { start: null, end: null },
      daysOfWeek: [],
      states: [],
      tracks: [],
      combinedOddsThreshold: null,
      horseCountRange: { min: null, max: null }
    };
    setLocalFilters(clearedFilters);
    if (onFiltersChange) {
      onFiltersChange(clearedFilters);
    }
  }, [onFiltersChange]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (localFilters.dateRange?.start || localFilters.dateRange?.end) count++;
    if (localFilters.daysOfWeek?.length > 0) count++;
    if (localFilters.states?.length > 0) count++;
    if (localFilters.tracks?.length > 0) count++;
    if (localFilters.combinedOddsThreshold !== null) count++;
    if (localFilters.horseCountRange?.min !== null || localFilters.horseCountRange?.max !== null) count++;
    return count;
  }, [localFilters]);

  return (
    <Card className={`p-4 ${className}`}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Filters</h3>
          {activeFilterCount > 0 && (
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
              {activeFilterCount} active
            </span>
          )}
        </div>

        {/* Date Range Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Date Range</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-500">From</Label>
              <Input
                type="date"
                value={localFilters.dateRange?.start || ''}
                onChange={(e) => handleDateRangeChange('start', e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">To</Label>
              <Input
                type="date"
                value={localFilters.dateRange?.end || ''}
                onChange={(e) => handleDateRangeChange('end', e.target.value)}
                className="text-sm"
              />
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Days of Week Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Days of Week</Label>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {daysOfWeekOptions.map(day => (
              <label key={day} className="flex items-center space-x-1">
                <input
                  type="checkbox"
                  checked={localFilters.daysOfWeek?.includes(day) || false}
                  onChange={(e) => handleMultiSelectChange('daysOfWeek', day, e.target.checked)}
                  className="rounded"
                />
                <span className="truncate">{day.slice(0, 3)}</span>
              </label>
            ))}
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* States Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">States</Label>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {stateOptions.map(state => (
              <label key={state} className="flex items-center space-x-1">
                <input
                  type="checkbox"
                  checked={localFilters.states?.includes(state) || false}
                  onChange={(e) => handleMultiSelectChange('states', state, e.target.checked)}
                  className="rounded"
                />
                <span>{state}</span>
              </label>
            ))}
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Tracks Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Tracks</Label>
          {trackOptions.length > 0 ? (
            <div className="max-h-32 overflow-y-auto space-y-1">
              {trackOptions.slice(0, 10).map(track => (
                <label key={track} className="flex items-center space-x-1 text-xs">
                  <input
                    type="checkbox"
                    checked={localFilters.tracks?.includes(track) || false}
                    onChange={(e) => handleMultiSelectChange('tracks', track, e.target.checked)}
                    className="rounded"
                  />
                  <span className="truncate">{track}</span>
                </label>
              ))}
              {trackOptions.length > 10 && (
                <div className="text-xs text-gray-500">
                  +{trackOptions.length - 10} more tracks available
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500">
              Track options will be loaded from data
            </div>
          )}
        </div>

        <hr className="border-gray-200" />

        {/* Combined Odds Threshold */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Combined Odds Threshold</Label>
          <Input
            type="number"
            step="0.1"
            placeholder="e.g., 10.0"
            value={localFilters.combinedOddsThreshold || ''}
            onChange={(e) => handleFilterChange('combinedOddsThreshold', 
              e.target.value ? parseFloat(e.target.value) : null)}
            className="text-sm"
          />
          {localFilters.combinedOddsThreshold && (
            <div className="text-xs text-gray-500">
              Showing races with combined odds ≤ {localFilters.combinedOddsThreshold}
            </div>
          )}
        </div>

        <hr className="border-gray-200" />

        {/* Horse Count Range */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Horse Count Range</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-500">Min</Label>
              <Input
                type="number"
                placeholder="Min"
                value={localFilters.horseCountRange?.min || ''}
                onChange={(e) => handleHorseCountChange('min', e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Max</Label>
              <Input
                type="number"
                placeholder="Max"
                value={localFilters.horseCountRange?.max || ''}
                onChange={(e) => handleHorseCountChange('max', e.target.value)}
                className="text-sm"
              />
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        {/* Clear Filters Button */}
        <Button 
          variant="outline" 
          size="sm" 
          onClick={clearAllFilters}
          className="w-full"
          disabled={activeFilterCount === 0}
        >
          Clear All Filters
        </Button>

        {/* Active Filters Summary */}
        {activeFilterCount > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Active Filters</Label>
            <div className="text-xs text-gray-600 space-y-1">
              {localFilters.dateRange?.start && (
                <div>From: {localFilters.dateRange.start}</div>
              )}
              {localFilters.dateRange?.end && (
                <div>To: {localFilters.dateRange.end}</div>
              )}
              {localFilters.daysOfWeek?.length > 0 && (
                <div>Days: {localFilters.daysOfWeek.join(', ')}</div>
              )}
              {localFilters.states?.length > 0 && (
                <div>States: {localFilters.states.join(', ')}</div>
              )}
              {localFilters.tracks?.length > 0 && (
                <div>Tracks: {localFilters.tracks.slice(0, 3).join(', ')}{localFilters.tracks.length > 3 ? '...' : ''}</div>
              )}
              {localFilters.combinedOddsThreshold && (
                <div>Odds ≤ {localFilters.combinedOddsThreshold}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SimpleFilterPanel;